import { GiftRedemptionsTable } from "@/components/GiftRedemptionsTable";
import CenterAgreements from "@/components/manager/CenterAgreements";
import RichContent from "@/components/RichContent";
import { useState, useEffect } from "react";
import { SpaceRequestsSection } from "@/components/manager/SpaceRequestsSection";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { LogOut, DoorOpen, Users, CalendarDays, DollarSign, Plus, Pencil, Trash2, Save, Loader2, MapPin, Clock, Building2, UserPlus, Check, X, Bell, CalendarPlus, CalendarX, AlertTriangle, CheckCheck, Repeat, Lock, Ticket, ShoppingBag, Sparkles, Banknote, MessageSquareQuote, ClipboardList, Percent } from "lucide-react";
import ManagerMembersPanel from "@/components/crm/ManagerMembersPanel";
import IntakeFormEditor from "@/components/enhancer/IntakeFormEditor";
import FormBuilderPanel from "@/components/FormBuilderPanel";
import TicketingEditor from "@/components/TicketingEditor";
import DashboardShell from "@/components/dashboard/DashboardShell";
import ContextSwitcher from "@/components/ContextSwitcher";
import RecurrencePicker from "@/components/RecurrencePicker";
import { useAuth } from "@/context/AuthContext";
import { useActiveContext } from "@/context/ActiveContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import DashboardHero from "@/components/DashboardHero";
import GoogleCalendarConnect from "@/components/GoogleCalendarConnect";
import PackageFormModal from "@/components/enhancer/PackageFormModal";
import AppointmentActions, { StatusBadge } from "@/components/AppointmentActions";
import SellerOrders from "@/components/SellerOrders";
import ProviderRosterPanel from "@/components/manager/ProviderRosterPanel";
import UnifiedServicesPanel from "@/components/manager/UnifiedServicesPanel";
import CouponsPanel from "@/components/manager/CouponsPanel";
import UnifiedCalendar from "@/components/UnifiedCalendar";
import BlackoutEditor from "@/components/manager/BlackoutEditor";
import ManagerReconciliationPanel from "@/components/manager/ManagerReconciliationPanel";
import CashDrawerPanel from "@/components/manager/CashDrawerPanel";
import CenterPayoutPanel from "@/components/manager/CenterPayoutPanel";
import RentalsPanel from "@/components/manager/RentalsPanel";
import PayrollPanel from "@/components/manager/PayrollPanel";
import OperationsHome from "@/components/manager/OperationsHome";
import RichTextEditor from "@/components/RichTextEditor";
import ImageUpload from "@/components/ImageUpload";
import BookingPolicyEditor from "@/components/BookingPolicyEditor";
import OperationsPanel from "@/pages/admin/OperationsPanel";
import InquiriesPanel from "@/components/InquiriesPanel";
import PersonalNotificationsPanel from "@/components/NotificationsPanel";
import { fmtDate, fmtTimeRange, money } from "@/lib/format";
import PosMovedCard from "@/components/PosMovedCard";
import RoleHome, { useRoleHome } from "@/components/dashboard/RoleHome";
import SetupChecklist from "@/components/dashboard/SetupChecklist";
import { BroadcastingHub } from "@/components/media/BroadcastingHub";
import { CENTER_FEATURES } from "@/lib/featureDescriptions";
import { RadioTower } from "lucide-react";
import { ProgramRoomBookings } from "@/components/programs/ProgramAdmin";
import { ClaimPanel } from "@/components/ClaimPanel";
import { ManagerReferralPanel } from "@/components/ReferralAgreements";

const TAB_LABEL = { "Day Close": "End-of-Day Close", Appointments: "Booking Rules" };

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const TABS = ["Operations", "Media & Broadcasting", "Notifications", "Inbox", "Inquiries", "Day Close", "Reconciliation", "Cash Drawer", "Payouts", "Rentals", "Payroll", "Appointments", "Approvals", "Location", "Calendar", "Blackouts", "Rooms", "Enhancers", "Members", "Intake Form", "Forms", "Agreements", "Services", "Coupons", "Events", "Roster", "Packages", "Bookings", "Orders"];

const ACCESS_LABELS = { discovery: "Discovery only", community: "Community only", all_members: "Members only" };
export function AccessBadge({ level }) {
  if (!level || level === "public") return null;
  return <span data-testid="access-badge" className="inline-flex items-center gap-0.5 text-[10px] uppercase px-2 py-0.5 rounded-full bg-[#144F75]/[0.08] text-[#144F75]"><Lock size={9} /> {ACCESS_LABELS[level] || "Members only"}</span>;
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="eyw-card p-6" data-testid={`mgr-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="text-3xl font-serif text-[#144F75]">{value}</span>
        {Icon && <Icon size={20} className="text-[#D98E73]" />}
      </div>
      <p className="text-sm text-[#4A5D6B] mt-1">{label}</p>
    </div>
  );
}

export default function LocationManagerDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return TABS.includes(t) ? t : "Operations";
  });
  const loc = useLocation();
  useEffect(() => {
    const t = new URLSearchParams(loc.search).get("tab");
    if (t && TABS.includes(t)) setTab(t);
  }, [loc.search]);
  const [focusApptId, setFocusApptId] = useState("");
  const isAdmin = ["admin", "staff"].includes(user?.role);

  // Active center + centers now come from the global context switcher, so the
  // Manager dashboard, POS and every other view stay in sync.
  const { activeCenterId: center, centers, setActiveCenter, centersLoading } = useActiveContext();

  const pickCenter = (id) => {
    setActiveCenter(id);
    if (id === "__all__") setTab("Operations");
    qc.invalidateQueries();
  };
  const isAll = center === "__all__";
  const visibleTabs = isAll ? ["Operations"] : TABS;

  const dash = useQuery({
    queryKey: ["mgr-dash", center],
    queryFn: () => api.get("/manager/me/dashboard").then((r) => r.data),
    enabled: !isAdmin || !!center,
  });
  const location = dash.data?.location;
  const notifQ = useQuery({
    queryKey: ["mgr-notif-unread", center],
    queryFn: () => api.get("/manager/me/notifications", { params: { limit: 1 } }).then((r) => r.data),
    enabled: (!isAdmin || !!center) && !!dash.data?.location,
    refetchInterval: 60000,
  });
  const unread = notifQ.data?.unread || 0;
  const home = useRoleHome("/manager/me/home", ["mgr-home", center]);

  const CenterPicker = () => (
    isAdmin && centers.length > 0 ? (
      <div className="relative mt-5 flex items-center gap-2" data-testid="mgr-center-picker">
        <Building2 size={15} className="text-white/80" />
        <select value={center} onChange={(e) => pickCenter(e.target.value)} data-testid="mgr-center-select"
          className="rounded-full bg-white/15 border border-white/30 text-white text-sm px-4 py-2 focus:outline-none [&>option]:text-[#1A242B]">
          <option value="__all__">All Centers (overview)</option>
          {centers.map((c) => <option key={c.id} value={c.id}>{c.name}{c.kind === "affiliate" ? " (Affiliate)" : ""}</option>)}
        </select>
      </div>
    ) : null
  );

  if (dash.isLoading || (isAdmin && centersLoading)) return <div className="min-h-[60vh] grid place-items-center text-[#4A5D6B]">Loading your center…</div>;

  if (!location) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="mgr-no-location">
        <Building2 size={40} className="mx-auto text-[#D98E73] mb-4" />
        <h1 className="font-serif text-3xl text-[#1A242B] mb-2">No center assigned yet</h1>
        <p className="text-[#4A5D6B] mb-6">{isAdmin ? "There are no centers to manage yet. Create one from the Admin → Locations panel." : "You don't manage a location yet. An ecosystem admin can assign you to a center from the Admin → Locations panel."}</p>
        <button onClick={() => nav(isAdmin ? "/admin" : "/")} className="eyw-btn !py-2.5 text-sm">{isAdmin ? "Go to Admin" : "Back to site"}</button>
      </div>
    );
  }

  const view = isAll ? "Operations" : tab;
  const TAB_ICON = {
    Operations: Building2, "Day Close": DollarSign, Appointments: CalendarDays, Calendar: CalendarDays, Blackouts: CalendarX,
    Approvals: CheckCheck, Services: Sparkles, Coupons: Percent,
    Events: CalendarDays, Packages: Ticket, Rooms: DoorOpen, Bookings: CalendarDays, Enhancers: Users,
    Roster: UserPlus, Reconciliation: DollarSign, "Cash Drawer": DollarSign, Payouts: Banknote,
    Rentals: Building2, Payroll: Banknote, Orders: ShoppingBag, Inbox: Bell, Inquiries: MessageSquareQuote, Location: MapPin,
    Members: Users, "Intake Form": ClipboardList, Forms: ClipboardList, Agreements: ClipboardList, "Media & Broadcasting": RadioTower,
  };
  const TAB_BADGE = {
    Inbox: unread, Services: dash.data?.pending_offering_requests || 0, Approvals: dash.data?.pending_approvals || 0,
  };
  const MGR_GROUPS = [
    { key: "overview", label: "Overview", icon: Building2, tabs: ["Operations", "Day Close"] },
    { key: "schedule", label: "Schedule & Front Desk", icon: CalendarDays, tabs: ["Calendar", "Appointments", "Blackouts", "Approvals"] },
    { key: "offerings", label: "Offerings & Facilities", icon: Sparkles, tabs: ["Services", "Events", "Packages", "Rooms", "Bookings"] },
    { key: "people", label: "People & Network", icon: Users, tabs: ["Enhancers", "Roster", "Members", "Intake Form", "Forms", "Agreements"] },
    { key: "finance", label: "Finance & Sales", icon: Banknote, tabs: ["Coupons", "Reconciliation", "Cash Drawer", "Payouts", "Rentals", "Payroll", "Orders"] },
    { key: "media", label: "Media & Broadcasting", icon: RadioTower, tabs: ["Media & Broadcasting"] },
    { key: "communication", label: "Communication & Feedback", icon: MessageSquareQuote, tabs: ["Notifications", "Inbox", "Inquiries"] },
    { key: "center", label: "Center", icon: MapPin, tabs: ["Location"] },
  ];
  const navGroups = MGR_GROUPS
    .map((g) => ({
      key: g.key, label: g.label, icon: g.icon,
      items: g.tabs.filter((t) => visibleTabs.includes(t)).map((t) => ({ key: t, label: TAB_LABEL[t] || t, icon: TAB_ICON[t], badge: TAB_BADGE[t] || 0, desc: CENTER_FEATURES[t] })),
    }))
    .filter((g) => g.items.length);
  const quickActions = [{ label: "Front Desk / POS", icon: Building2, onClick: () => nav("/pos"), testid: "mgr-pos-btn", primary: true }];

  return (
    <DashboardShell
      role="location_manager"
      brandTitle={location.name}
      scopeName={`${location.kind === "affiliate" ? "Affiliate Center" : "Flagship Center"}${isAll ? " · All Centers" : ""}`}
      nav={navGroups}
      activeKey={view}
      onSelect={setTab}
      contextSwitcher={isAdmin ? <ContextSwitcher /> : null}
      quickActions={quickActions}
      user={user}
      onLogout={() => { logout(); nav("/"); }}
    >
      <div data-testid="location-manager-dashboard">

      <div key={center || "me"}>
      {view === "Operations" && !isAll && (
        <RoleHome endpoint="/manager/me/home" queryKey={["mgr-home", center]} title={location.name} showUpcoming={false}
          go={(k) => (k === "unread" ? setTab("Inbox") : setTab(k === "approvals" ? "Approvals" : "Bookings"))}
          quickLinks={[
            { label: "Front desk / POS", onClick: () => nav("/pos") },
            { label: "Calendar", onClick: () => setTab("Calendar") },
            { label: "Bookings", onClick: () => setTab("Bookings") },
            { label: "Services", onClick: () => setTab("Services") },
            { label: "Events", onClick: () => setTab("Events") },
            { label: "Members", onClick: () => setTab("Members") },
            { label: "Media & Broadcasting", onClick: () => setTab("Media & Broadcasting") },
            { label: "Messages", onClick: () => nav("/messages") },
          ]}>
          {home.data?.checklist && <SetupChecklist role="center" steps={home.data.checklist.steps} dismissed={home.data.checklist.dismissed} go={setTab} />}
        </RoleHome>
      )}
      {view === "Media & Broadcasting" && <BroadcastingHub />}
      {view === "Operations" && (
        <OperationsHome
          center={center}
          onOpenAppointment={(bid) => { setFocusApptId(bid); setTab("Appointments"); }}
          onGoTab={(t) => setTab(t)}
        />
      )}

      {view === "Location" && <LocationEditor location={location} onSaved={() => { qc.invalidateQueries({ queryKey: ["mgr-dash"] }); }} />}
      {view === "Roster" && <ProviderRosterPanel />}
      {view === "Day Close" && <OperationsPanel endpoint="/manager/operations" />}
      {view === "Reconciliation" && <ManagerReconciliationPanel />}
      {view === "Cash Drawer" && <CashDrawerPanel />}
      {view === "Payouts" && <><CenterPayoutPanel /><ClaimPanel base="/manager" testid="center-claim" /><GiftRedemptionsTable endpoint="/manager/me/gift-card-redemptions" title="Gift card redemptions at your center" /></>}
      {view === "Rentals" && <RentalsPanel />}
      {view === "Payroll" && <PayrollPanel />}
      {view === "Appointments" && (
        <div className="space-y-5">
          <PosMovedCard what="Booking, rescheduling and payments for appointments" testid="mgr-pos-moved" />
          <BookingPolicyEditor base="manager" testid="mgr-booking-policy" />
        </div>
      )}
      {view === "Rooms" && <RoomsPanel />}
      {view === "Enhancers" && <><EnhancersPanel pending={dash.data.pending_enhancers} onChange={() => qc.invalidateQueries({ queryKey: ["mgr-dash"] })} /><ManagerReferralPanel /></>}
      {view === "Services" && (
        <div className="space-y-12">
          <UnifiedServicesPanel />
          <OfferingRequestsPanel />
        </div>
      )}
      {view === "Notifications" && <PersonalNotificationsPanel />}
      {view === "Inbox" && <NotificationsPanel onRead={() => qc.invalidateQueries({ queryKey: ["mgr-notif-unread", center] })} onOpen={(bid) => { setFocusApptId(bid); setTab("Appointments"); }} />}
      {view === "Inquiries" && <InquiriesPanel basePath="/manager" title="Inquiries" subtitle="Messages from visitors and prospective members who found this center — reply here and they'll get it by email." />}
      {view === "Coupons" && <CouponsPanel />}
      {view === "Approvals" && <AppointmentApprovalsPanel />}
      {view === "Events" && <CenterEventsPanel />}
      {view === "Calendar" && (
        <div className="space-y-8">
          <UnifiedCalendar scope="center" />
          <GoogleCalendarConnect />
        </div>
      )}
      {view === "Blackouts" && <BlackoutEditor />}
      {view === "Packages" && <PackagesPanel />}
      {view === "Bookings" && <><BookingsPanel /><ProgramRoomBookings /></>}
      {view === "Orders" && <SellerOrders />}
      {view === "Members" && <ManagerMembersPanel />}
      {view === "Intake Form" && <IntakeFormEditor endpoint="/manager/me/intake-form" subtitle="New clients at your center complete these questions before their first session. Enhancer-specific questions are merged in automatically." />}
      {view === "Forms" && <FormBuilderPanel />}
      {view === "Agreements" && <CenterAgreements />}
      </div>
      </div>
    </DashboardShell>
  );
}

const ProfileCompletion = () => {
  const { data } = useQuery({ queryKey: ["mgr-loc-completion"], queryFn: () => api.get("/manager/me/location/completion").then((r) => r.data) });
  if (!data) return null;
  return (
    <div className={`eyw-card p-4 mb-4 ${data.complete ? "border-[#5C8A72]/40 bg-[#5C8A72]/[0.06]" : ""}`} data-testid="mgr-loc-completion">
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="font-medium text-[#1A242B]" data-testid="mgr-loc-completion-status">{data.complete ? "Profile complete" : `Profile ${data.pct}% complete`}</span>
        <span className="text-xs text-[#8FA89B]">{data.pct}%</span>
      </div>
      <div className="h-2 rounded-full bg-[#E5E0D8] overflow-hidden mt-2"><div className="h-full bg-[#5C8A72] transition-[width]" style={{ width: `${data.pct}%` }} /></div>
      {!data.complete && <p className="text-xs text-[#4A5D6B] mt-2" data-testid="mgr-loc-completion-missing">Still needed: {data.missing.join(", ")}{data.missing.includes("Rooms") ? " (add rooms in the Rooms tab)" : ""}</p>}
    </div>
  );
};

const GalleryAdd = ({ onAdd }) => {
  const [draft, setDraft] = useState("");
  const set = (url) => { if (url && url.includes("/api/files/")) { onAdd(url); setDraft(""); } else setDraft(url); };
  return (
    <div className="mt-3 space-y-2">
      <ImageUpload value={draft} onChange={set} label="Add a gallery photo" testid="mgr-loc-gallery-add" />
      {draft && <button type="button" onClick={() => { onAdd(draft.trim()); setDraft(""); }} className="eyw-btn-ghost !py-1.5 text-sm" data-testid="mgr-loc-gallery-add-confirm">Add this photo</button>}
    </div>
  );
};

const CenterPhotos = ({ form, setForm }) => {
  const gal = form.gallery || [];
  const setGal = (g) => setForm({ ...form, gallery: g });
  return (
    <div data-testid="mgr-loc-photos">
      <label className="text-sm font-medium text-[#1A242B] mb-1 block">Cover photo <span className="text-xs text-[#8FA89B] font-normal">— shown on your page and in the center directory</span></label>
      <ImageUpload value={form.image} onChange={(url) => setForm({ ...form, image: url })} label="" testid="mgr-loc-cover" />
      <label className="text-sm font-medium text-[#1A242B] mt-4 mb-1 block">Gallery</label>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {gal.map((u, i) => (
          <div key={i} className="relative" data-testid={`mgr-loc-gallery-${i}`}>
            <img src={u} alt="" className="h-28 w-full object-cover rounded-xl" />
            <button type="button" onClick={() => setGal(gal.filter((_, j) => j !== i))} aria-label="Remove photo" data-testid={`mgr-loc-gallery-remove-${i}`}
              className="absolute top-1.5 right-1.5 h-8 w-8 grid place-items-center rounded-full bg-white/90 text-[#b8694f]"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
      <GalleryAdd onAdd={(url) => setGal([...gal, url])} />
    </div>
  );
};

function LocationEditor({ location, onSaved }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    name: location.name || "", description: location.description || "", image: location.image || "",
    address: location.address || "", city: location.city || "", state: location.state || "",
    zip: location.zip || "", phone: location.phone || "", email: location.email || "",
    website: location.website || "", youtube_channel_id: location.youtube_channel_id || "",
    google_review_url: location.google_review_url || "",
    social_links: location.social_links || {},
    gallery: location.gallery || [], amenitiesText: (location.amenities || []).join(", "),
    videosText: (location.videos || []).join("\n"),
    hours: (location.hours && location.hours.length ? location.hours : DAYS.map((_, d) => ({ day: d, open: "09:00", close: "18:00", closed: d === 0 }))),
  });
  const [saving, setSaving] = useState(false);
  const setHour = (i, k, v) => setForm({ ...form, hours: form.hours.map((h, idx) => idx === i ? { ...h, [k]: v } : h) });
  const setSocial = (k, v) => setForm({ ...form, social_links: { ...form.social_links, [k]: v } });
  const save = async () => {
    setSaving(true);
    try {
      const { videosText, amenitiesText, ...rest } = form;
      const payload = { ...rest, videos: (videosText || "").split("\n").map((s) => s.trim()).filter(Boolean),
        gallery: (form.gallery || []).filter(Boolean), amenities: (amenitiesText || "").split(",").map((s) => s.trim()).filter(Boolean) };
      await api.put("/manager/me/location", payload); toast.success("Location updated"); onSaved();
      qc.invalidateQueries({ queryKey: ["mgr-loc-completion"] }); qc.invalidateQueries({ queryKey: ["mgr-home"] });
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const field = (label, key, ph = "") => (
    <div><label className="text-sm font-medium text-[#1A242B] mb-1 block">{label}</label>
      <input value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} placeholder={ph} data-testid={`mgr-loc-${key}`} className="eyw-input w-full" /></div>
  );
  return (
    <div className="max-w-3xl" data-testid="mgr-location-editor">
      <h2 className="font-serif text-2xl text-[#1A242B] mb-5">Location details</h2>
      <ProfileCompletion />
      <div className="eyw-card p-4 sm:p-6 space-y-4">
        {field("Center name", "name")}
        <CenterPhotos form={form} setForm={setForm} />
        <div><label className="text-sm font-medium text-[#1A242B] mb-1 block">Description</label>
          <RichTextEditor value={form.description} onChange={(v) => setForm({ ...form, description: v })} testid="mgr-loc-description" placeholder="Describe your center — add headings, images and video…" minHeight={150} /></div>
        <div className="grid md:grid-cols-2 gap-4">
          {field("Address", "address")}{field("City", "city")}
          {field("State", "state")}{field("Zip", "zip")}
          {field("Phone", "phone")}{field("Email", "email")}
        </div>
        <div>
          <label className="text-sm font-medium text-[#1A242B] mb-2 flex items-center gap-2"><Clock size={15} /> Hours of operation</label>
          <div className="space-y-2">
            {form.hours.map((h, i) => (
              <div key={i} className="flex items-center gap-3 flex-wrap" data-testid={`mgr-hours-${i}`}>
                <span className="w-10 text-sm text-[#4A5D6B]">{DAYS[h.day]}</span>
                <label className="flex items-center gap-1.5 text-xs text-[#4A5D6B]">
                  <input type="checkbox" checked={!h.closed} onChange={(e) => setHour(i, "closed", !e.target.checked)} data-testid={`mgr-hours-open-${i}`} /> Open
                </label>
                {!h.closed && (<>
                  <input type="time" value={h.open} onChange={(e) => setHour(i, "open", e.target.value)} className="eyw-input !py-1.5 text-sm" />
                  <span className="text-[#4A5D6B] text-sm">to</span>
                  <input type="time" value={h.close} onChange={(e) => setHour(i, "close", e.target.value)} className="eyw-input !py-1.5 text-sm" />
                </>)}
                {h.closed && <span className="text-xs text-[#8FA89B]">Closed</span>}
              </div>
            ))}
          </div>
        </div>
        <div><label className="text-sm font-medium text-[#1A242B] mb-1 block">Amenities (separate with commas)</label>
          <input value={form.amenitiesText} onChange={(e) => setForm({ ...form, amenitiesText: e.target.value })} placeholder="Free parking, Showers, Tea lounge, Wheelchair access" data-testid="mgr-loc-amenities" className="eyw-input w-full" /></div>
        <div className="pt-2 border-t border-[#E5E0D8]">
          <h3 className="font-serif text-lg text-[#1A242B] mb-3">Links & media</h3>
          <div className="space-y-3">
            {field("Website", "website", "https://…")}
            {field("Google review link (post-visit feedback follow-ups send happy members here)", "google_review_url", "https://g.page/r/…")}
            <div className="grid md:grid-cols-3 gap-3">
              <div><label className="text-sm font-medium text-[#1A242B] mb-1 block">Instagram</label>
                <input value={form.social_links.instagram || ""} onChange={(e) => setSocial("instagram", e.target.value)} placeholder="https://instagram.com/…" data-testid="mgr-loc-instagram" className="eyw-input w-full" /></div>
              <div><label className="text-sm font-medium text-[#1A242B] mb-1 block">Facebook</label>
                <input value={form.social_links.facebook || ""} onChange={(e) => setSocial("facebook", e.target.value)} placeholder="https://facebook.com/…" data-testid="mgr-loc-facebook" className="eyw-input w-full" /></div>
              <div><label className="text-sm font-medium text-[#1A242B] mb-1 block">YouTube</label>
                <input value={form.social_links.youtube || ""} onChange={(e) => setSocial("youtube", e.target.value)} placeholder="https://youtube.com/@…" data-testid="mgr-loc-youtube" className="eyw-input w-full" /></div>
            </div>
            <div><label className="text-sm font-medium text-[#1A242B] mb-1 block">Featured videos (one YouTube/Vimeo URL per line)</label>
              <textarea value={form.videosText} onChange={(e) => setForm({ ...form, videosText: e.target.value })} rows={3} placeholder="https://youtu.be/…&#10;https://vimeo.com/…" data-testid="mgr-loc-videos" className="eyw-input w-full font-mono text-xs" /></div>
            <div><label className="text-sm font-medium text-[#1A242B] mb-1 block">YouTube channel ID (auto-pulls latest videos when a YouTube API key is configured)</label>
              <input value={form.youtube_channel_id} onChange={(e) => setForm({ ...form, youtube_channel_id: e.target.value })} placeholder="UCxxxxxxxxxxxxxxxxxxxxxx" data-testid="mgr-loc-channel-id" className="eyw-input w-full" />
              <p className="text-xs text-[#8FA89B] mt-1">Optional. An admin adds the YouTube Data API key in Admin → Integrations to switch this on.</p></div>
          </div>
        </div>
        <div className="flex justify-end">
          <button onClick={save} disabled={saving} data-testid="mgr-loc-save" className="eyw-btn !py-2.5 text-sm disabled:opacity-60">{saving && <Loader2 className="animate-spin" size={16} />} <Save size={15} /> Save</button>
        </div>
      </div>
    </div>
  );
}

function RoomsPanel() {
  const qc = useQueryClient();
  const rooms = useQuery({ queryKey: ["mgr-rooms"], queryFn: () => api.get("/manager/me/rooms").then((r) => r.data) });
  const [modal, setModal] = useState(null);
  const del = async (id) => { if (!window.confirm("Delete this room?")) return; await api.delete(`/manager/rooms/${id}`); toast.success("Room deleted"); qc.invalidateQueries({ queryKey: ["mgr-rooms"] }); qc.invalidateQueries({ queryKey: ["mgr-dash"] }); };
  return (
    <div data-testid="mgr-rooms-panel">
      <div className="flex flex-wrap gap-3 justify-between items-center mb-5">
        <h2 className="font-serif text-2xl text-[#1A242B]">Rooms & spaces</h2>
        <button onClick={() => setModal({})} data-testid="mgr-add-room" className="eyw-btn !py-2 text-sm"><Plus size={16} /> Add room</button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {(rooms.data || []).map((r) => (
          <div key={r.id} className="eyw-card p-6" data-testid={`mgr-room-${r.id}`}>
            <div className="flex justify-between items-start">
              <h3 className="text-lg text-[#1A242B] flex items-center gap-2"><DoorOpen size={16} className="text-[#144F75]" /> {r.name}</h3>
              <span className="text-xs px-2 py-0.5 rounded-full bg-[#F1EDE6] text-[#4A5D6B]">{r.space_type === "retreat_space" ? "Retreat" : "Center"}</span>
            </div>
            <p className="text-xs text-[#4A5D6B] mt-2">Capacity: {r.capacity || "—"} {r.requires_approval && "· needs approval"}</p>
            {r.description && <p className="text-xs text-[#8FA89B] mt-1">{r.description}</p>}
            <div className="flex gap-2 mt-4">
              <button onClick={() => setModal(r)} className="eyw-btn-ghost !py-1.5 !px-3 text-xs"><Pencil size={13} /> Edit</button>
              <button onClick={() => del(r.id)} className="eyw-btn-ghost !py-1.5 !px-3 text-xs !border-red-300 !text-red-600 hover:!bg-red-500"><Trash2 size={13} /> Delete</button>
            </div>
          </div>
        ))}
        {rooms.data?.length === 0 && <p className="text-[#4A5D6B] text-sm">No rooms yet. Add your first treatment room or space.</p>}
      </div>
      {modal && <RoomModal room={modal.id ? modal : null} allRooms={rooms.data || []} onClose={() => setModal(null)} onSaved={() => { qc.invalidateQueries({ queryKey: ["mgr-rooms"] }); qc.invalidateQueries({ queryKey: ["mgr-dash"] }); }} />}
    </div>
  );
}

function RoomModal({ room, allRooms = [], onClose, onSaved }) {
  const [form, setForm] = useState({
    name: room?.name || "", description: room?.description || "", capacity: room?.capacity || 0,
    space_type: room?.space_type || "wellness_center", requires_approval: room?.requires_approval || false, active: true,
    conflicts_with: room?.conflicts_with || [],
  });
  const [saving, setSaving] = useState(false);
  // Other rooms at this center that can be linked as mutually exclusive.
  const others = allRooms.filter((r) => r.key && (!room || r.id !== room.id));
  const toggleConflict = (key) => setForm((f) => ({
    ...f,
    conflicts_with: f.conflicts_with.includes(key) ? f.conflicts_with.filter((k) => k !== key) : [...f.conflicts_with, key],
  }));
  const save = async () => {
    if (!form.name.trim()) return toast.error("Please add a name to continue.");
    setSaving(true);
    try {
      if (room) await api.put(`/manager/rooms/${room.id}`, form);
      else await api.post("/manager/rooms", form);
      toast.success("Room saved"); onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="mgr-room-modal">
        <h3 className="font-serif text-xl text-[#1A242B] mb-4">{room ? "Edit room" : "New room"}</h3>
        <div className="space-y-3">
          <div><label className="text-sm font-medium mb-1 block">Name</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="mgr-room-name" className="eyw-input w-full" /></div>
          <div><label className="text-sm font-medium mb-1 block">Description</label><input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="eyw-input w-full" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-sm font-medium mb-1 block">Capacity</label><input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value) || 0 })} className="eyw-input w-full" /></div>
            <div><label className="text-sm font-medium mb-1 block">Type</label>
              <select value={form.space_type} onChange={(e) => setForm({ ...form, space_type: e.target.value })} className="eyw-input w-full">
                <option value="wellness_center">Wellness room</option>
                <option value="retreat_space">Retreat space</option>
              </select></div>
          </div>
          <label className="flex items-center gap-2 text-sm text-[#4A5D6B]"><input type="checkbox" checked={form.requires_approval} onChange={(e) => setForm({ ...form, requires_approval: e.target.checked })} /> Reservations require approval</label>
          {others.length > 0 && (
            <div data-testid="mgr-room-conflicts">
              <label className="text-sm font-medium mb-1 block">Blocks these rooms when in use</label>
              <p className="text-[11px] text-[#8FA89B] mb-2">When this room is booked, the selected rooms are auto-blocked for that time — and vice-versa (the block is mutual).</p>
              <div className="space-y-1.5 rounded-lg border border-[#E5E0D8] p-2.5 bg-[#F9F7F3] max-h-40 overflow-y-auto">
                {others.map((r) => (
                  <label key={r.id} data-testid={`mgr-room-conflict-${r.key}`} className="flex items-center gap-2 text-sm text-[#2A3942] cursor-pointer">
                    <input type="checkbox" checked={form.conflicts_with.includes(r.key)} onChange={() => toggleConflict(r.key)} className="accent-[#144F75]" />
                    <DoorOpen size={13} className="text-[#144F75]" /> {r.name}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="eyw-btn-ghost !py-2 text-sm">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="mgr-room-save" className="eyw-btn !py-2 text-sm disabled:opacity-60">{saving && <Loader2 className="animate-spin" size={15} />} Save</button>
        </div>
      </div>
    </div>
  );
}

const NOTIF_META = {
  booking: { icon: CalendarPlus, color: "text-[#5C8A72]", bg: "bg-[#5C8A72]/12" },
  cancelled: { icon: CalendarX, color: "text-red-500", bg: "bg-red-100" },
  rescheduled: { icon: CalendarDays, color: "text-[#144F75]", bg: "bg-[#144F75]/10" },
  reassigned: { icon: Users, color: "text-[#144F75]", bg: "bg-[#144F75]/10" },
  review_flag: { icon: AlertTriangle, color: "text-[#B5794E]", bg: "bg-[#D98E73]/18" },
};

function NotificationsPanel({ onRead, onOpen }) {
  const qc = useQueryClient();
  const notif = useQuery({ queryKey: ["mgr-notifications"], queryFn: () => api.get("/manager/me/notifications", { params: { limit: 100 } }).then((r) => r.data) });
  const items = notif.data?.items || [];
  const markRead = async (id) => { await api.post(`/manager/notifications/${id}/read`); qc.invalidateQueries({ queryKey: ["mgr-notifications"] }); onRead?.(); };
  const markAll = async () => { await api.post("/manager/notifications/read-all"); toast.success("All caught up"); qc.invalidateQueries({ queryKey: ["mgr-notifications"] }); onRead?.(); };
  const open = (n) => {
    if (!n.booking_id) return;
    if (!n.read) markRead(n.id);
    onOpen?.(n.booking_id);
  };
  const timeAgo = (iso) => { try { const d = new Date(iso); const m = Math.floor((Date.now() - d.getTime()) / 60000); if (m < 1) return "just now"; if (m < 60) return `${m}m ago`; const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`; return d.toLocaleDateString(); } catch { return ""; } };
  return (
    <div data-testid="mgr-notifications-panel">
      <div className="flex justify-between items-center mb-2">
        <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><Bell size={20} className="text-[#144F75]" /> Center inbox</h2>
        {items.some((n) => !n.read) && <button onClick={markAll} data-testid="mgr-notif-read-all" className="eyw-btn-ghost !py-1.5 !px-3 text-xs"><CheckCheck size={14} /> Mark all read</button>}
      </div>
      <p className="text-sm text-[#4A5D6B] mb-5">Every booking, cancellation, reschedule, and provider review flag at your center. Tap an item to jump straight to the appointment.</p>
      <div className="eyw-card divide-y divide-[#E5E0D8]">
        {notif.isLoading ? (
          <div className="p-6 text-center"><Loader2 className="animate-spin text-[#144F75] inline" /></div>
        ) : items.length === 0 ? (
          <p className="text-[#4A5D6B] text-sm p-6 text-center" data-testid="mgr-notif-empty">No activity yet. New bookings and changes will show up here.</p>
        ) : items.map((n) => {
          const meta = NOTIF_META[n.kind] || NOTIF_META.booking;
          const Icon = meta.icon;
          const clickable = !!n.booking_id;
          return (
            <div key={n.id} data-testid={`mgr-notif-${n.id}`} onClick={() => open(n)}
              className={`p-4 flex items-start gap-3 ${n.read ? "" : "bg-[#FBF8F3]"} ${clickable ? "cursor-pointer hover:bg-[#F4F1EA]" : ""}`}>
              <div className={`shrink-0 h-9 w-9 rounded-full grid place-items-center ${meta.bg}`}><Icon size={16} className={meta.color} /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-[#1A242B]">{n.title}</span>
                  {!n.read && <span className="h-2 w-2 rounded-full bg-[#D98E73]" />}
                </div>
                <p className="text-xs text-[#4A5D6B] mt-0.5">{n.body}</p>
                <p className="text-[11px] text-[#8FA89B] mt-1">{timeAgo(n.created_at)}{clickable ? " · tap to open" : ""}</p>
              </div>
              {!n.read && <button onClick={(e) => { e.stopPropagation(); markRead(n.id); }} data-testid={`mgr-notif-read-${n.id}`} className="shrink-0 text-xs text-[#144F75] hover:underline">Mark read</button>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AppointmentApprovalsPanel() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["mgr-appt-approvals"], queryFn: () => api.get("/manager/me/appointment-approvals").then((r) => r.data) });
  const [target, setTarget] = useState(null);
  const pending = data?.pending || [];
  const decided = data?.decided || [];
  const refresh = () => { qc.invalidateQueries({ queryKey: ["mgr-appt-approvals"] }); qc.invalidateQueries({ queryKey: ["mgr-dash"] }); };
  return (
    <div data-testid="mgr-approvals-panel">
      <SpaceRequestsSection />
      <h2 className="font-serif text-2xl text-[#1A242B] mb-1">Appointment approvals</h2>
      <p className="text-sm text-[#4A5D6B] mb-5">In-center appointment requests await your approval. Approving confirms them for the client; declining notifies the client with your reason and frees the slot.</p>
      {isLoading ? (
        <div className="p-8 text-center"><Loader2 className="animate-spin text-[#144F75] inline" /></div>
      ) : (
        <>
          <h3 className="text-sm font-semibold text-[#1A242B] mb-2">Pending ({pending.length})</h3>
          {pending.length === 0 ? (
            <p className="text-[#4A5D6B] text-sm mb-6" data-testid="mgr-approvals-empty">No appointments awaiting approval.</p>
          ) : (
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {pending.map((b) => (
                <div key={b.id} className="eyw-card p-5" data-testid={`mgr-approval-${b.id}`}>
                  <h4 className="font-serif text-lg text-[#1A242B]">{b.service_title || "Appointment"}</h4>
                  <p className="text-xs text-[#4A5D6B] mt-0.5">{b.name || b.user_email || "Guest"} · {fmtDate(b.date)} at {fmtTimeRange(b.start_time)} · {b.duration_min}m</p>
                  {b.room && <p className="text-[11px] text-[#8FA89B] mt-1">Room: {b.room}</p>}
                  <div className="flex gap-2 mt-4">
                    <button onClick={() => setTarget({ b, action: "approve" })} data-testid={`mgr-approval-approve-${b.id}`} className="eyw-btn !py-1.5 !px-3 text-xs !bg-[#5C8A72] hover:!bg-[#4d7561]"><Check size={13} /> Approve</button>
                    <button onClick={() => setTarget({ b, action: "decline" })} data-testid={`mgr-approval-decline-${b.id}`} className="eyw-btn-ghost !py-1.5 !px-3 text-xs !border-red-300 !text-red-600"><X size={13} /> Decline</button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {decided.length > 0 && (
            <>
              <h3 className="text-sm font-semibold text-[#1A242B] mb-2">Recently decided</h3>
              <div className="eyw-card divide-y divide-[#E5E0D8]">
                {decided.map((b) => (
                  <div key={b.id} className="p-4 flex items-center justify-between gap-3" data-testid={`mgr-approval-decided-${b.id}`}>
                    <div>
                      <div className="text-sm font-semibold text-[#1A242B]">{b.service_title} <span className="text-xs font-normal text-[#8FA89B]">· {fmtDate(b.date)} {fmtTimeRange(b.start_time)}</span></div>
                      {b.decline_reason && <div className="text-xs text-[#8FA89B] mt-0.5">Reason: {b.decline_reason}</div>}
                    </div>
                    <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${b.approval_state === "approved" ? "bg-[#5C8A72]/15 text-[#5C8A72]" : "bg-red-100 text-red-600"}`}>{b.approval_state}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
      {target && <ApprovalDecideModal target={target} onClose={() => setTarget(null)} onDone={() => { setTarget(null); refresh(); }} />}
    </div>
  );
}

function ApprovalDecideModal({ target, onClose, onDone }) {
  const { b, action } = target;
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (action === "decline" && !note.trim()) return toast.error("Please add a reason for the client");
    setBusy(true);
    try {
      await api.post(`/manager/me/appointments/${b.id}/decide`, { action, note });
      toast.success(action === "approve" ? "Appointment confirmed — client notified" : "Appointment declined — client notified");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[110] grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="mgr-approval-modal">
        <h3 className="font-serif text-xl text-[#1A242B] mb-1">{action === "approve" ? "Approve appointment" : "Decline appointment"}</h3>
        <p className="text-sm text-[#4A5D6B] mb-4">“{b.service_title}” · {fmtDate(b.date)} at {fmtTimeRange(b.start_time)}</p>
        <div>
          <label className="text-sm font-medium mb-1 block">{action === "decline" ? "Reason for the client (required)" : "Note (optional)"}</label>
          <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} data-testid="mgr-approval-note" className="eyw-input w-full resize-none" placeholder={action === "decline" ? "Let the client know why…" : "Anything to add…"} />
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="eyw-btn-ghost !py-2 text-sm">Cancel</button>
          <button onClick={go} disabled={busy} data-testid="mgr-approval-confirm" className={`eyw-btn !py-2 text-sm disabled:opacity-60 ${action === "decline" ? "!bg-red-600 hover:!bg-red-700" : "!bg-[#5C8A72] hover:!bg-[#4d7561]"}`}>{busy && <Loader2 className="animate-spin" size={15} />} {action === "approve" ? "Approve" : "Decline"}</button>
        </div>
      </div>
    </div>
  );
}

function OfferingRequestsPanel() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["mgr-offering-requests"], queryFn: () => api.get("/manager/me/offering-requests").then((r) => r.data) });
  const rooms = useQuery({ queryKey: ["mgr-rooms"], queryFn: () => api.get("/manager/me/rooms").then((r) => r.data) });
  const [decideTarget, setDecideTarget] = useState(null);
  const pending = data?.pending || [];
  const decided = data?.decided || [];
  const refresh = () => { qc.invalidateQueries({ queryKey: ["mgr-offering-requests"] }); qc.invalidateQueries({ queryKey: ["mgr-dash"] }); };
  return (
    <div data-testid="mgr-offering-requests-panel">
      <h2 className="font-serif text-2xl text-[#1A242B] mb-1">Offering requests</h2>
      <p className="text-sm text-[#4A5D6B] mb-5">Enhancers asking to deliver their own custom offering <b>in person at your center</b>, propose a new service, or join a service's provider roster. Approve to make it bookable here, or decline — declined offerings stay online/virtual on the enhancer's profile.</p>
      <ServiceApprovalsSection />
      {isLoading ? (
        <div className="p-8 text-center"><Loader2 className="animate-spin text-[#144F75] inline" /></div>
      ) : (
        <>
          <h3 className="text-sm font-semibold text-[#1A242B] mb-2">Pending ({pending.length})</h3>
          {pending.length === 0 ? (
            <p className="text-[#4A5D6B] text-sm mb-6" data-testid="mgr-offering-empty">No pending requests right now.</p>
          ) : (
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {pending.map((s) => (
                <div key={s.id} className="eyw-card p-5" data-testid={`mgr-offering-${s.id}`}>
                  <h4 className="font-serif text-lg text-[#1A242B]">{s.title}</h4>
                  <p className="text-xs text-[#4A5D6B] mt-0.5">{s.enhancer_name || "Enhancer"} · {s.category} · {s.duration_min}m · {money(Number(s.price || 0))}</p>
                  {s.short_description && <p className="text-sm text-[#8FA89B] mt-2 line-clamp-2">{s.short_description}</p>}
                  <div className="flex gap-2 mt-4">
                    <button onClick={() => setDecideTarget({ svc: s, action: "approve" })} data-testid={`mgr-offering-approve-${s.id}`} className="eyw-btn !py-1.5 !px-3 text-xs !bg-[#5C8A72] hover:!bg-[#4d7561]"><Check size={13} /> Approve</button>
                    <button onClick={() => setDecideTarget({ svc: s, action: "reject" })} data-testid={`mgr-offering-reject-${s.id}`} className="eyw-btn-ghost !py-1.5 !px-3 text-xs !border-red-300 !text-red-600"><X size={13} /> Decline</button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {decided.length > 0 && (
            <>
              <h3 className="text-sm font-semibold text-[#1A242B] mb-2">Decided</h3>
              <div className="eyw-card divide-y divide-[#E5E0D8]">
                {decided.map((s) => (
                  <div key={s.id} className="p-4 flex items-center justify-between gap-3" data-testid={`mgr-offering-decided-${s.id}`}>
                    <div>
                      <div className="text-sm font-semibold text-[#1A242B]">{s.title} <span className="text-xs font-normal text-[#8FA89B]">· {s.enhancer_name}</span></div>
                      {s.center_host_note && <div className="text-xs text-[#8FA89B] mt-0.5">Note: {s.center_host_note}</div>}
                    </div>
                    <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${s.center_host_status === "approved" ? "bg-[#5C8A72]/15 text-[#5C8A72]" : "bg-red-100 text-red-600"}`}>{s.center_host_status}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
      {decideTarget && <OfferingDecideModal target={decideTarget} rooms={rooms.data || []} onClose={() => setDecideTarget(null)} onDone={() => { setDecideTarget(null); refresh(); }} />}
    </div>
  );
}

function OfferingDecideModal({ target, rooms = [], onClose, onDone }) {
  const { svc, action } = target;
  const [note, setNote] = useState("");
  const [room, setRoom] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (action === "reject" && !note.trim()) return toast.error("Please add a short note for the enhancer");
    setBusy(true);
    try {
      await api.post(`/manager/offering-requests/${svc.id}/decide`, { action, note, room });
      toast.success(action === "approve" ? "Offering approved for in-person hosting" : "Offering declined — enhancer notified");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[110] grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="mgr-offering-decide-modal">
        <h3 className="font-serif text-xl text-[#1A242B] mb-1">{action === "approve" ? "Approve offering" : "Decline offering"}</h3>
        <p className="text-sm text-[#4A5D6B] mb-4">“{svc.title}” by {svc.enhancer_name || "the enhancer"}</p>
        {action === "approve" && (
          <div className="mb-3">
            <label className="text-sm font-medium mb-1 block">Assign a room <span className="text-[11px] text-[#8FA89B] font-normal">(optional)</span></label>
            <select value={room} onChange={(e) => setRoom(e.target.value)} data-testid="mgr-offering-room" className="eyw-input w-full">
              <option value="">No specific room</option>
              {rooms.map((r) => <option key={r.id} value={r.key}>{r.name}</option>)}
            </select>
          </div>
        )}
        <div>
          <label className="text-sm font-medium mb-1 block">{action === "reject" ? "Reason / note (required)" : "Note to enhancer (optional)"}</label>
          <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} data-testid="mgr-offering-note" className="eyw-input w-full resize-none" placeholder={action === "reject" ? "Let them know why, so they can adjust…" : "Anything they should know…"} />
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="eyw-btn-ghost !py-2 text-sm">Cancel</button>
          <button onClick={go} disabled={busy} data-testid="mgr-offering-decide-confirm" className={`eyw-btn !py-2 text-sm disabled:opacity-60 ${action === "reject" ? "!bg-red-600 hover:!bg-red-700" : "!bg-[#5C8A72] hover:!bg-[#4d7561]"}`}>{busy && <Loader2 className="animate-spin" size={15} />} {action === "approve" ? "Approve" : "Decline"}</button>
        </div>
      </div>
    </div>
  );
}

function ServiceApprovalsSection() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["mgr-service-proposals"], queryFn: () => api.get("/manager/me/service-proposals").then((r) => r.data) });
  const proposals = data?.proposals || [];
  const requests = data?.roster_requests || [];
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["mgr-service-proposals"] });
    qc.invalidateQueries({ queryKey: ["mgr-provider-roster"] });
    qc.invalidateQueries({ queryKey: ["mgr-sp-roster"] });
    qc.invalidateQueries({ queryKey: ["mgr-my-services"] });
  };
  const decideProposal = async (sid, approve) => {
    try { await api.put(`/manager/services/${sid}/approval`, { approve }); toast.success(approve ? "Service approved & published" : "Proposal rejected"); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const decideRoster = async (aid, approve) => {
    try { await api.put(`/manager/roster-requests/${aid}`, { approve }); toast.success(approve ? "Enhancer added to the roster" : "Request declined"); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (proposals.length === 0 && requests.length === 0) return null;
  return (
    <div className="mb-8" data-testid="mgr-service-approvals-section">
      {proposals.length > 0 && (
        <>
          <h3 className="text-sm font-semibold text-[#1A242B] mb-2">Proposed services ({proposals.length})</h3>
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            {proposals.map((p) => (
              <div key={p.id} className="eyw-card p-5" data-testid={`mgr-proposal-${p.id}`}>
                <h4 className="font-serif text-lg text-[#1A242B]">{p.title}</h4>
                <p className="text-xs text-[#4A5D6B] mt-0.5">{p.enhancer_name || "Enhancer"} · {p.category} · {p.duration_min}m · {money(Number(p.price || 0))}</p>
                {p.short_description && <p className="text-sm text-[#8FA89B] mt-2 line-clamp-2">{p.short_description}</p>}
                <div className="flex gap-2 mt-4">
                  <button onClick={() => decideProposal(p.id, true)} data-testid={`mgr-proposal-approve-${p.id}`} className="eyw-btn !py-1.5 !px-3 text-xs !bg-[#5C8A72] hover:!bg-[#4d7561]"><Check size={13} /> Approve & publish</button>
                  <button onClick={() => decideProposal(p.id, false)} data-testid={`mgr-proposal-reject-${p.id}`} className="eyw-btn-ghost !py-1.5 !px-3 text-xs !border-red-300 !text-red-600"><X size={13} /> Reject</button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      {requests.length > 0 && (
        <>
          <h3 className="text-sm font-semibold text-[#1A242B] mb-2">Roster requests ({requests.length})</h3>
          <div className="eyw-card divide-y divide-[#E5E0D8] mb-6">
            {requests.map((r) => (
              <div key={r.id} className="p-4 flex items-center justify-between gap-3" data-testid={`mgr-roster-req-${r.id}`}>
                <div>
                  <div className="text-sm font-semibold text-[#1A242B]">{r.enhancer_name || "Enhancer"}</div>
                  <div className="text-xs text-[#8FA89B]">wants to offer “{r.service_title}”</div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => decideRoster(r.id, true)} data-testid={`mgr-roster-approve-${r.id}`} className="eyw-btn !py-1.5 !px-3 text-xs !bg-[#5C8A72] hover:!bg-[#4d7561]"><Check size={13} /> Approve</button>
                  <button onClick={() => decideRoster(r.id, false)} data-testid={`mgr-roster-reject-${r.id}`} className="eyw-btn-ghost !py-1.5 !px-3 text-xs !border-red-300 !text-red-600"><X size={13} /> Decline</button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function CenterEventsPanel() {
  const qc = useQueryClient();
  const events = useQuery({ queryKey: ["mgr-my-events"], queryFn: () => api.get("/manager/me/events").then((r) => r.data) });
  const rooms = useQuery({ queryKey: ["mgr-rooms"], queryFn: () => api.get("/manager/me/rooms").then((r) => r.data) });
  const [modal, setModal] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const roomName = (key) => (rooms.data || []).find((r) => r.key === key)?.name || key;
  const refresh = () => qc.invalidateQueries({ queryKey: ["mgr-my-events"] });
  const delOne = async (id) => { if (!window.confirm("Delete this event?")) return; try { await api.delete(`/manager/events/${id}`); toast.success("Event deleted"); refresh(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (
    <div data-testid="mgr-events-panel">
      <div className="flex flex-wrap gap-3 justify-between items-center mb-5">
        <h2 className="font-serif text-2xl text-[#1A242B]">Events at your center</h2>
        <button onClick={() => setModal({})} data-testid="mgr-add-event" className="eyw-btn !py-2 text-sm"><Plus size={16} /> New event</button>
      </div>
      <div className="eyw-card divide-y divide-[#E5E0D8]">
        {(events.data || []).map((ev) => (
          <div key={ev.id} className="p-4 flex items-center justify-between gap-3" data-testid={`mgr-event-${ev.id}`}>
            <div>
              <div className="text-sm font-semibold text-[#1A242B] flex items-center gap-1.5">{ev.title}
                {ev.series_id && <span data-testid={`mgr-event-series-${ev.id}`} className="inline-flex items-center gap-0.5 text-[10px] uppercase text-[#B5794E]"><Repeat size={11} /> series</span>}
                <AccessBadge level={ev.access_level} />
                {ev.is_private && <span data-testid={`mgr-event-private-badge-${ev.id}`} className="inline-flex items-center gap-0.5 text-[10px] uppercase px-2 py-0.5 rounded-full bg-[#9E5C7F]/12 text-[#9E5C7F]"><Lock size={9} /> Private</span>}
                {!ev.published && <span className="text-[10px] uppercase text-[#8FA89B]">· draft</span>}
              </div>
              <div className="text-xs text-[#4A5D6B]">{fmtDate(ev.date)} · {fmtTimeRange(ev.start_time, ev.end_time)} · {roomName(ev.room)} · {money(Number(ev.price || 0))}</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setModal(ev)} data-testid={`mgr-event-edit-${ev.id}`} className="eyw-btn-ghost !py-1.5 !px-3 text-xs"><Pencil size={13} /> Edit</button>
              <button onClick={() => (ev.series_id ? setDelTarget(ev) : delOne(ev.id))} data-testid={`mgr-event-del-${ev.id}`} className="text-red-500 hover:text-red-600"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
        {events.data?.length === 0 && <p className="text-[#4A5D6B] text-sm p-4">No events yet. Create your first circle, class, or workshop.</p>}
      </div>
      {modal && <EventModal event={modal.id ? modal : null} rooms={rooms.data || []} onClose={() => setModal(null)} onSaved={refresh} />}
      {delTarget && <EventDeleteModal event={delTarget} onClose={() => setDelTarget(null)} onDeleted={() => { setDelTarget(null); refresh(); }} />}
    </div>
  );
}

function EventDeleteModal({ event, onClose, onDeleted }) {
  const [scope, setScope] = useState("one");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/manager/events/${event.id}?scope=${scope}`);
      toast.success(`Deleted ${data.deleted} event${data.deleted === 1 ? "" : "s"}`);
      onDeleted();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const opt = (val, label) => (
    <button onClick={() => setScope(val)} data-testid={`mgr-event-del-scope-${val}`}
      className={`w-full text-left rounded-xl border px-3 py-2.5 text-sm ${scope === val ? "border-[#144F75] bg-[#144F75]/[0.05] text-[#144F75] font-medium" : "border-[#E5E0D8] text-[#4A5D6B]"}`}>{label}</button>
  );
  return (
    <div className="fixed inset-0 z-[110] grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()} data-testid="mgr-event-delete-modal">
        <h3 className="font-serif text-xl text-[#1A242B] mb-1">Delete recurring event</h3>
        <p className="text-sm text-[#4A5D6B] mb-4 flex items-center gap-1.5"><Repeat size={14} className="text-[#B5794E]" /> “{event.title}” is part of a series. Apply to:</p>
        <div className="space-y-2">
          {opt("one", "Just this one")}
          {opt("future", "This & upcoming occurrences")}
          {opt("all", "The entire series")}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="eyw-btn-ghost !py-2 text-sm">Cancel</button>
          <button onClick={go} disabled={busy} data-testid="mgr-event-delete-confirm" className="rounded-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm disabled:opacity-60 flex items-center gap-1.5">{busy && <Loader2 className="animate-spin" size={15} />} Delete</button>
        </div>
      </div>
    </div>
  );
}

function EventModal({ event, rooms, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: event?.title || "", category: event?.category || "Circle", facilitator: event?.facilitator || "",
    room: event?.room || (rooms[0]?.key || ""), date: event?.date || "", start_time: event?.start_time || "18:00",
    end_time: event?.end_time || "20:00", description: event?.description || "", price: event?.price || 0,
    tiers: event?.tiers || [], meal_options: event?.meal_options || [], lodging_options: event?.lodging_options || [],
    image: event?.image || "",
    capacity: event?.capacity || 20, published: event?.published ?? true,
    access_level: event?.access_level || "public",
    is_private: event?.is_private || false,
    invite_scope: event?.invite_scope || "open",
    invite_group_ids: event?.invite_group_ids || [],
    invite_member_ids: event?.invite_member_ids || [],
    enhancer_id: event?.enhancer_id || event?.facilitator_id || "",
    enhancer_ids: (event?.facilitator_ids || []).filter((id) => id !== (event?.enhancer_id || event?.facilitator_id)),
  });
  const [recurrence, setRecurrence] = useState(null);
  const [override, setOverride] = useState(false);
  const [scope, setScope] = useState("one");
  const [repeatMode, setRepeatMode] = useState("frequency"); // frequency | custom
  const [customDates, setCustomDates] = useState([]); // [{date, start_time, end_time}]
  const [saving, setSaving] = useState(false);
  const isSeries = !!event?.series_id;

  // Approved Enhancers qualified at this center (roster).
  const { data: sp } = useQuery({ queryKey: ["mgr-sp-roster"], queryFn: () => api.get("/manager/me/service-practitioners").then((r) => r.data) });
  const roster = sp?.roster || [];
  const { data: mgrDash } = useQuery({ queryKey: ["mgr-dashboard"], queryFn: () => api.get("/manager/me/dashboard").then((r) => r.data) });
  const centerName = mgrDash?.location?.name || "Your center";
  const toggleCoFacilitator = (id) => setForm((f) => ({ ...f, enhancer_ids: f.enhancer_ids.includes(id) ? f.enhancer_ids.filter((x) => x !== id) : [...f.enhancer_ids, id] }));

  // Invitation targeting (private events): groups to invite + individual member search.
  const [memberQ, setMemberQ] = useState("");
  const { data: allGroups = [] } = useQuery({
    queryKey: ["mgr-invite-groups"], enabled: form.is_private,
    queryFn: () => api.get("/groups").then((r) => r.data),
  });
  const { data: allMembers = [] } = useQuery({
    queryKey: ["mgr-invite-members"], enabled: form.is_private && form.invite_scope === "individual",
    queryFn: () => api.get("/manager/members").then((r) => r.data),
  });
  const memberName = (id) => allMembers.find((m) => m.id === id)?.name || id;
  const groupName = (id) => allGroups.find((g) => g.id === id)?.name || id;
  const memberMatches = memberQ.trim()
    ? allMembers.filter((m) => (m.name || "").toLowerCase().includes(memberQ.trim().toLowerCase()) && !form.invite_member_ids.includes(m.id)).slice(0, 8)
    : [];
  const toggleGroup = (id) => setForm((f) => ({ ...f, invite_group_ids: f.invite_group_ids.includes(id) ? f.invite_group_ids.filter((x) => x !== id) : [...f.invite_group_ids, id] }));
  const addMember = (id) => { setForm((f) => ({ ...f, invite_member_ids: f.invite_member_ids.includes(id) ? f.invite_member_ids : [...f.invite_member_ids, id] })); setMemberQ(""); };
  const removeMember = (id) => setForm((f) => ({ ...f, invite_member_ids: f.invite_member_ids.filter((x) => x !== id) }));

  // Live room availability for the chosen room + date (includes auto-blocks from linked rooms).
  const { data: avail } = useQuery({
    queryKey: ["mgr-event-avail", form.room, form.date],
    queryFn: () => api.get(`/venue/availability?room_key=${form.room}&date=${form.date}`).then((r) => r.data),
    enabled: !!form.room && !!form.date,
  });
  const toMin = (t) => { const [h, m] = String(t).split(":"); return (parseInt(h) || 0) * 60 + (parseInt(m) || 0); };
  const overlaps = (avail?.busy || []).filter((b) => toMin(form.start_time) < toMin(b.end) && toMin(b.start) < toMin(form.end_time));
  const hasClash = overlaps.length > 0;

  const save = async () => {
    if (!form.title.trim() || !form.date) return toast.error("Title and date are required");
    if (!form.room) return toast.error("Choose a room at your center");
    setSaving(true);
    try {
      const payload = { ...form, recurrence, override };
      if (!event && repeatMode === "custom") {
        const cds = customDates.filter((c) => c.date);
        if (cds.length === 0) { setSaving(false); return toast.error("Add at least one specific date"); }
        payload.custom_dates = cds;
        payload.recurrence = null;
        payload.date = cds[0].date;
        payload.start_time = cds[0].start_time || form.start_time;
        payload.end_time = cds[0].end_time || form.end_time;
      }
      if (event) {
        const { data } = await api.put(`/manager/events/${event.id}?scope=${isSeries ? scope : "one"}`, payload);
        toast.success(data && data.series ? `Updated ${data.updated} event${data.updated === 1 ? "" : "s"}` : "Event saved");
      } else {
        const { data } = await api.post("/manager/events", payload);
        if (data.occurrences_created != null) {
          const skip = (data.occurrences_skipped || []).length;
          toast.success(`Created ${data.occurrences_created} event${data.occurrences_created === 1 ? "" : "s"}${skip ? ` · ${skip} skipped (room conflict)` : ""}`);
        } else {
          toast.success("Event saved");
        }
      }
      onSaved(); onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((d && d.message) ? d.message : formatApiError(d));
    }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="mgr-event-modal">
        <h3 className="font-serif text-xl text-[#1A242B] mb-4">{event ? "Edit event" : "New event"}</h3>
        <div className="space-y-3">
          {isSeries && (
            <div className="rounded-xl bg-[#F9F7F3] border border-[#E5E0D8] p-3" data-testid="mgr-event-edit-scope">
              <p className="text-xs text-[#4A5D6B] mb-2 flex items-center gap-1.5"><Repeat size={13} className="text-[#B5794E]" /> Recurring event — apply changes to:</p>
              <div className="flex gap-1.5">
                {[["one", "This one"], ["future", "This & upcoming"], ["all", "Entire series"]].map(([v, l]) => (
                  <button key={v} onClick={() => setScope(v)} data-testid={`mgr-event-edit-scope-${v}`}
                    className={`flex-1 rounded-full py-1.5 text-[11px] border ${scope === v ? "bg-[#144F75] text-white border-[#144F75]" : "border-[#E5E0D8] text-[#4A5D6B]"}`}>{l}</button>
                ))}
              </div>
              {scope !== "one" && <p className="text-[10px] text-[#8FA89B] mt-1.5">Each occurrence keeps its own date; time & details update across the series.</p>}
            </div>
          )}
          <div><label className="text-sm font-medium mb-1 block">Title</label><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="mgr-event-title" className="eyw-input w-full" /></div>
          <div><label className="text-sm font-medium mb-1 block">Location</label>
            <select value="__own" disabled data-testid="mgr-event-location" className="eyw-input w-full bg-[#F1EEE8] text-[#4A5D6B] cursor-not-allowed">
              <option value="__own">{centerName}</option>
            </select>
            <p className="text-[11px] text-[#8FA89B] mt-1">Events are hosted at your center. Rooms below are scoped to this location.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-sm font-medium mb-1 block">Date</label><input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="mgr-event-date" className="eyw-input w-full" /></div>
            <div><label className="text-sm font-medium mb-1 block">Room</label>
              <select value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} data-testid="mgr-event-room" className="eyw-input w-full">
                <option value="">Choose room…</option>
                {rooms.map((r) => <option key={r.id} value={r.key}>{r.name}</option>)}
              </select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-sm font-medium mb-1 block">Start</label><input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="eyw-input w-full" /></div>
            <div><label className="text-sm font-medium mb-1 block">End</label><input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="eyw-input w-full" /></div>
          </div>
          <div><label className="text-sm font-medium mb-1 block">Primary Enhancer</label>
            <select value={form.enhancer_id} onChange={(e) => setForm({ ...form, enhancer_id: e.target.value, enhancer_ids: form.enhancer_ids.filter((x) => x !== e.target.value) })} data-testid="mgr-event-enhancer" className="eyw-input w-full">
              <option value="">Unassigned</option>
              {roster.map((r) => <option key={r.enhancer_id} value={r.enhancer_id}>{r.name}</option>)}
            </select>
            <p className="text-[11px] text-[#8FA89B] mt-1">Only Enhancers approved at your center. They're shown publicly, notified on sign-ups, and credited in payouts.</p>
          </div>
          {form.enhancer_id && roster.filter((r) => r.enhancer_id !== form.enhancer_id).length > 0 && (
            <div data-testid="mgr-event-cofacilitators">
              <label className="text-sm font-medium mb-1 block">Co-facilitators <span className="text-[11px] text-[#8FA89B] font-normal">(optional — others who practice this)</span></label>
              <div className="flex flex-wrap gap-1.5">
                {roster.filter((r) => r.enhancer_id !== form.enhancer_id).map((r) => (
                  <button key={r.enhancer_id} type="button" onClick={() => toggleCoFacilitator(r.enhancer_id)} data-testid={`mgr-event-cofac-${r.enhancer_id}`}
                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs border ${form.enhancer_ids.includes(r.enhancer_id) ? "bg-[#5C8A72] text-white border-[#5C8A72]" : "border-[#E5E0D8] text-[#4A5D6B]"}`}>
                    {r.name}{form.enhancer_ids.includes(r.enhancer_id) && <Check size={11} />}
                  </button>
                ))}
              </div>
            </div>
          )}
          {hasClash && (
            <div className="rounded-lg bg-[#FBEEE8] border border-[#E7C3B3] px-3 py-2.5 text-xs text-[#8A4B2E]" data-testid="mgr-event-conflict-warning">
              <p className="font-semibold flex items-center gap-1.5"><AlertTriangle size={13} /> This room is busy at that time</p>
              {overlaps.map((b, i) => (
                <p key={i} className="mt-0.5">{b.start}–{b.end} · {b.label}{b.blocked_by ? "" : ""}</p>
              ))}
              <p className="mt-1 text-[#B5794E]">Change the time, or tick “Book anyway” below to override.</p>
            </div>
          )}
          {!event && (
            <div className="rounded-xl border border-[#E5E0D8] p-3" data-testid="mgr-event-schedule-mode">
              <div className="flex gap-1.5 mb-3">
                {[["frequency", "Frequency"], ["custom", "Specific dates"]].map(([v, l]) => (
                  <button key={v} onClick={() => setRepeatMode(v)} data-testid={`mgr-event-mode-${v}`}
                    className={`flex-1 rounded-full py-1.5 text-xs border ${repeatMode === v ? "bg-[#144F75] text-white border-[#144F75]" : "border-[#E5E0D8] text-[#4A5D6B]"}`}>{l}</button>
                ))}
              </div>
              {repeatMode === "frequency" ? (
                <div>
                  <label className="text-xs font-medium mb-1 block flex items-center gap-1.5"><Repeat size={13} className="text-[#144F75]" /> Repeat</label>
                  <RecurrencePicker value={recurrence} onChange={setRecurrence} testid="mgr-event-recurrence" />
                  <p className="text-[10px] text-[#8FA89B] mt-1">Uses the Date & Start/End above.</p>
                </div>
              ) : (
                <div data-testid="mgr-event-custom-dates">
                  <label className="text-xs font-medium mb-1 block">Specific dates & times</label>
                  {customDates.map((c, i) => (
                    <div key={i} className="flex items-center gap-1.5 mb-1.5">
                      <input type="date" value={c.date} onChange={(e) => setCustomDates(customDates.map((x, j) => j === i ? { ...x, date: e.target.value } : x))} data-testid={`mgr-event-cd-date-${i}`} className="eyw-input flex-1 !py-1.5 text-xs" />
                      <input type="time" value={c.start_time} onChange={(e) => setCustomDates(customDates.map((x, j) => j === i ? { ...x, start_time: e.target.value } : x))} className="eyw-input !py-1.5 text-xs w-24" />
                      <input type="time" value={c.end_time} onChange={(e) => setCustomDates(customDates.map((x, j) => j === i ? { ...x, end_time: e.target.value } : x))} className="eyw-input !py-1.5 text-xs w-24" />
                      <button onClick={() => setCustomDates(customDates.filter((_, j) => j !== i))} className="text-red-500 shrink-0"><Trash2 size={14} /></button>
                    </div>
                  ))}
                  <button onClick={() => setCustomDates([...customDates, { date: "", start_time: form.start_time, end_time: form.end_time }])} data-testid="mgr-event-cd-add"
                    className="eyw-btn-ghost !py-1.5 text-xs mt-1"><Plus size={13} /> Add date</button>
                </div>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-sm font-medium mb-1 block">Price ($)</label><input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })} className="eyw-input w-full" /></div>
            <div><label className="text-sm font-medium mb-1 block">Capacity</label><input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value) || 0 })} className="eyw-input w-full" /></div>
          </div>
          <TicketingEditor tiers={form.tiers} meals={form.meal_options} lodging={form.lodging_options} onChange={(k, v) => setForm({ ...form, [k]: v })} />
          <div><label className="text-sm font-medium mb-1 block">Description</label>
            <RichTextEditor value={form.description} onChange={(html) => setForm({ ...form, description: html })} testid="mgr-event-description" enableBlocks={false} placeholder="Describe the experience — add headings, lists, images and video…" minHeight={140} /></div>
          <ImageUpload value={form.image} onChange={(url) => setForm({ ...form, image: url })} label="Event photo (square)" crop="square" testid="mgr-event-image" />
          <div><label className="text-sm font-medium mb-1 block">Access level</label>
            <select value={form.access_level} onChange={(e) => setForm({ ...form, access_level: e.target.value })} data-testid="mgr-event-access" className="eyw-input w-full">
              <option value="public">Public — any guest</option>
              <option value="discovery">Discovery members only</option>
              <option value="community">Community members only</option>
              <option value="all_members">All members</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-[#4A5D6B]"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Published</label>
          <label className="flex items-center gap-2 text-sm text-[#4A5D6B]" data-testid="mgr-event-private-label"><input type="checkbox" checked={form.is_private} onChange={(e) => setForm({ ...form, is_private: e.target.checked })} data-testid="mgr-event-private" /> Private event <span className="text-[11px] text-[#8FA89B]">(hidden from guests; members see a placeholder unless they have access)</span></label>
          {form.is_private && (
            <div className="rounded-xl border border-[#9E5C7F]/25 bg-[#9E5C7F]/6 p-3 space-y-3" data-testid="mgr-event-invite-box">
              <p className="text-xs font-semibold text-[#9E5C7F] flex items-center gap-1.5"><Lock size={12} /> Who can see & register</p>
              <div className="space-y-1.5">
                {[["open", "Open to qualifying members", "Anyone who meets the access level above"],
                  ["group", "Invite by group", "Only members of the groups you choose"],
                  ["individual", "Invite individually", "Only the specific members you pick"]].map(([v, l, d]) => (
                  <label key={v} className={`flex items-start gap-2 rounded-lg border p-2 cursor-pointer ${form.invite_scope === v ? "border-[#9E5C7F] bg-white" : "border-[#E5E0D8]"}`} data-testid={`mgr-event-invite-scope-${v}`}>
                    <input type="radio" name="invite_scope" checked={form.invite_scope === v} onChange={() => setForm({ ...form, invite_scope: v })} className="mt-0.5" />
                    <span><span className="text-sm text-[#1A242B]">{l}</span><span className="block text-[11px] text-[#8FA89B]">{d}</span></span>
                  </label>
                ))}
              </div>

              {form.invite_scope === "group" && (
                <div data-testid="mgr-event-invite-groups">
                  <label className="text-xs font-medium mb-1 block text-[#4A5D6B]">Invited groups</label>
                  {allGroups.length === 0 ? <p className="text-[11px] text-[#8FA89B]">No groups yet.</p> : (
                    <div className="flex flex-wrap gap-1.5">
                      {allGroups.map((g) => (
                        <button key={g.id} type="button" onClick={() => toggleGroup(g.id)} data-testid={`mgr-event-group-opt-${g.id}`}
                          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs border ${form.invite_group_ids.includes(g.id) ? "bg-[#9E5C7F] text-white border-[#9E5C7F]" : "border-[#E5E0D8] text-[#4A5D6B]"}`}>
                          <Users size={11} /> {g.name}{form.invite_group_ids.includes(g.id) && <Check size={11} />}
                        </button>
                      ))}
                    </div>
                  )}
                  <p className="text-[10px] text-[#8FA89B] mt-1.5">All current members of the selected group(s) get a notification with a direct link.</p>
                </div>
              )}

              {form.invite_scope === "individual" && (
                <div data-testid="mgr-event-invite-members">
                  <label className="text-xs font-medium mb-1 block text-[#4A5D6B]">Invited members</label>
                  {form.invite_member_ids.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {form.invite_member_ids.map((id) => (
                        <span key={id} data-testid={`mgr-event-invitee-${id}`} className="inline-flex items-center gap-1 rounded-full bg-[#9E5C7F] text-white px-2.5 py-1 text-xs">
                          {memberName(id)}
                          <button type="button" onClick={() => removeMember(id)} data-testid={`mgr-event-invitee-remove-${id}`}><X size={11} /></button>
                        </span>
                      ))}
                    </div>
                  )}
                  <input value={memberQ} onChange={(e) => setMemberQ(e.target.value)} placeholder="Search members by name…" data-testid="mgr-event-member-search" className="eyw-input w-full !py-1.5 text-sm" />
                  {memberMatches.length > 0 && (
                    <div className="mt-1 rounded-lg border border-[#E5E0D8] bg-white divide-y divide-[#F0EDE6] max-h-40 overflow-y-auto">
                      {memberMatches.map((m) => (
                        <button key={m.id} type="button" onClick={() => addMember(m.id)} data-testid={`mgr-event-member-opt-${m.id}`}
                          className="w-full text-left px-3 py-1.5 text-sm text-[#4A5D6B] hover:bg-[#9E5C7F]/8">{m.name}<span className="text-[11px] text-[#8FA89B] ml-1">{m.role}</span></button>
                      ))}
                    </div>
                  )}
                  <p className="text-[10px] text-[#8FA89B] mt-1.5">Each invited member gets a notification with a direct link. Newly added members are notified on save.</p>
                </div>
              )}
            </div>
          )}
          {hasClash && (
            <label className="flex items-center gap-2 text-sm text-[#8A4B2E] font-medium" data-testid="mgr-event-override-label">
              <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} data-testid="mgr-event-override" /> Book anyway (override room conflict)
            </label>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="eyw-btn-ghost !py-2 text-sm">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="mgr-event-save" className="eyw-btn !py-2 text-sm disabled:opacity-60">{saving && <Loader2 className="animate-spin" size={15} />} Save</button>
        </div>
      </div>
    </div>
  );
}


function EnhancersPanel({ onChange }) {
  const qc = useQueryClient();
  const roster = useQuery({ queryKey: ["mgr-enhancers"], queryFn: () => api.get("/manager/me/enhancers").then((r) => r.data) });
  const [email, setEmail] = useState("");
  const [adding, setAdding] = useState(false);
  const refresh = () => { qc.invalidateQueries({ queryKey: ["mgr-enhancers"] }); onChange?.(); };
  const recruit = async () => {
    if (!email.trim()) return;
    setAdding(true);
    try { await api.post("/manager/enhancers", { email: email.trim() }); toast.success("Enhancer added to your roster"); setEmail(""); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAdding(false); }
  };
  const decide = async (id, action) => {
    try {
      const { data } = await api.post(`/manager/enhancers/${id}/decide`, { action });
      const msg = { approve: "Approved", reject: "Declined", pause: "Paused", revoke: "Revoked", restore: "Restored" }[action] || "Updated";
      toast.success(data.appointments_flagged ? `${msg} — ${data.appointments_flagged} appointment(s) flagged for review` : msg);
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const [rates, setRates] = useState({});
  const approveWithRate = async (e) => {
    const r = parseFloat(rates[e.id]);
    if (rates[e.id] && !(r > 0 && r <= 90)) { toast.error("Referral rate must be between 0 and 90% of the sale."); return; }
    await decide(e.id, "approve");
    if (r > 0) {
      try { await api.post("/manager/referral-agreements", { enhancer_id: e.enhancer_id, rate_pct: r }); toast.success(`${r}% referral rate offered — active once they confirm.`); qc.invalidateQueries({ queryKey: ["mgr-referrals"] }); }
      catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    }
  };
  const remove = async (id) => { if (!window.confirm("Remove this enhancer from your location?")) return; await api.delete(`/manager/enhancers/${id}`); toast.success("Removed"); refresh(); };
  const badge = (s) => s === "approved" ? "bg-[#5C8A72]/15 text-[#5C8A72]" : s === "rejected" || s === "revoked" ? "bg-red-100 text-red-600" : s === "paused" ? "bg-[#D98E73]/25 text-[#B5794E]" : "bg-[#D98E73]/15 text-[#B5794E]";
  return (
    <div data-testid="mgr-enhancers-panel">
      <h2 className="font-serif text-2xl text-[#1A242B] mb-2">Enhancers at your center</h2>
      <p className="text-sm text-[#4A5D6B] mb-5">Invite enhancers by email (they start <b>Pending</b> and complete their profile &amp; qualifications), then approve them. Pause or revoke to stop new bookings while keeping their records — affected upcoming appointments are flagged for review, never silently changed.</p>
      <div className="eyw-card p-4 flex flex-wrap items-center gap-3 mb-6 max-w-xl">
        <UserPlus size={18} className="text-[#144F75]" />
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="enhancer@email.com" data-testid="mgr-recruit-email" className="eyw-input flex-1 min-w-0" />
        <button onClick={recruit} disabled={adding} data-testid="mgr-recruit-btn" className="eyw-btn !py-2 text-sm disabled:opacity-60">{adding && <Loader2 className="animate-spin" size={15} />} Invite</button>
      </div>
      <div className="eyw-card divide-y divide-[#E5E0D8]">
        {(roster.data || []).map((e) => (
          <div key={e.id} className="p-4 flex items-center justify-between gap-3" data-testid={`mgr-enhancer-${e.id}`}>
            <div className="flex items-center gap-3 min-w-0">
              {e.enhancer_avatar
                ? <img src={e.enhancer_avatar} alt={e.enhancer_name || "Enhancer"} data-testid={`mgr-enhancer-avatar-${e.id}`} className="h-10 w-10 rounded-full object-cover shrink-0" />
                : <span data-testid={`mgr-enhancer-avatar-${e.id}`} className="grid place-items-center h-10 w-10 rounded-full bg-[#144F75]/10 text-[#144F75] text-sm font-semibold shrink-0">{(e.enhancer_name || e.enhancer_email || "?").charAt(0).toUpperCase()}</span>}
              <div className="min-w-0">
                <div className="text-sm font-semibold text-[#1A242B] truncate">{e.enhancer_name || e.enhancer_email || "Unnamed Enhancer"}</div>
                <div className="text-xs text-[#4A5D6B] truncate">{e.enhancer_email}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <span className={`text-xs px-2.5 py-1 rounded-full ${badge(e.status)}`} data-testid={`mgr-enhancer-status-${e.id}`}>{e.status}</span>
              {e.status === "pending" && (<>
                <input type="number" min="0" max="90" step="0.5" value={rates[e.id] || ""} onChange={(ev) => setRates({ ...rates, [e.id]: ev.target.value })}
                  placeholder="Referral % (optional)" title="Optional referral rate: % of each center-delivered sale this enhancer refers" data-testid={`mgr-approve-rate-${e.id}`} className="eyw-input !py-1 !px-2 text-xs w-36" />
                <button onClick={() => approveWithRate(e)} data-testid={`mgr-approve-${e.id}`} className="eyw-btn !py-1 !px-3 text-xs"><Check size={13} /> {rates[e.id] ? "Approve & offer" : "Approve"}</button>
                <button onClick={() => decide(e.id, "reject")} className="eyw-btn-ghost !py-1 !px-3 text-xs"><X size={13} /> Decline</button>
              </>)}
              {e.status === "approved" && (
                <button onClick={() => decide(e.id, "pause")} data-testid={`mgr-pause-${e.id}`} className="eyw-btn-ghost !py-1 !px-3 text-xs">Pause</button>
              )}
              {e.status === "paused" && (
                <button onClick={() => decide(e.id, "restore")} data-testid={`mgr-restore-${e.id}`} className="eyw-btn !py-1 !px-3 text-xs">Restore</button>
              )}
              {["approved", "paused"].includes(e.status) && (
                <button onClick={() => { if (window.confirm("Revoke this Enhancer's approval at your center? Their records are kept; new bookings stop and upcoming appointments are flagged for review.")) decide(e.id, "revoke"); }} data-testid={`mgr-revoke-${e.id}`} className="text-xs px-3 py-1 rounded-full border border-red-300 text-red-600 hover:bg-red-50">Revoke</button>
              )}
              {e.status === "revoked" && (
                <button onClick={() => decide(e.id, "approve")} data-testid={`mgr-reapprove-${e.id}`} className="eyw-btn !py-1 !px-3 text-xs">Re-approve</button>
              )}
              <button onClick={() => remove(e.id)} className="text-red-500 hover:text-red-600"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
        {roster.data?.length === 0 && <p className="text-[#4A5D6B] text-sm p-4">No enhancers on your roster yet.</p>}
      </div>
    </div>
  );
}

function BookingsPanel() {
  const qc = useQueryClient();
  const bookings = useQuery({ queryKey: ["mgr-bookings"], queryFn: () => api.get("/manager/me/bookings").then((r) => r.data) });
  return (
    <div data-testid="mgr-bookings-panel">
      <h2 className="font-serif text-2xl text-[#1A242B] mb-1">Bookings at your center</h2>
      <p className="text-xs text-[#4A5D6B] mb-5">Confirm requests, or reschedule if a room/time isn't available — move it &amp; notify, or propose a time the client accepts.</p>
      <div className="eyw-card divide-y divide-[#E5E0D8]">
        {(bookings.data || []).map((b) => (
          <div key={b.id} className="p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`mgr-booking-${b.id}`}>
            <div>
              <div className="text-sm font-semibold text-[#1A242B] flex items-center gap-2"><CalendarDays size={14} /> {b.service_title}</div>
              <div className="text-xs text-[#4A5D6B]">{b.name} · {fmtDate(b.date)} · {fmtTimeRange(b.start_time)} · {b.room}{b.status === "reschedule_proposed" && b.proposed_date ? ` → proposed ${b.proposed_date} ${b.proposed_time}` : ""}</div>
            </div>
            <div className="flex items-center gap-3">
              <StatusBadge status={b.status} />
              <AppointmentActions booking={b} onChanged={() => qc.invalidateQueries({ queryKey: ["mgr-bookings"] })} />
            </div>
          </div>
        ))}
        {bookings.data?.length === 0 && <p className="text-[#4A5D6B] text-sm p-4">No bookings at your center yet.</p>}
      </div>
    </div>
  );
}


function PackagesPanel() {
  const qc = useQueryClient();
  const [modal, setModal] = useState(null);
  const packages = useQuery({ queryKey: ["mgr-packages"], queryFn: () => api.get("/manager/me/packages").then((r) => r.data) });
  const refetch = () => qc.invalidateQueries({ queryKey: ["mgr-packages"] });
  const del = async (id) => {
    if (!window.confirm("Delete this package? This cannot be undone.")) return;
    await api.delete(`/manager/me/packages/${id}`); toast.success("Package deleted"); refetch();
  };
  return (
    <div data-testid="mgr-packages-panel">
      <div className="flex flex-wrap gap-3 justify-between items-center mb-5">
        <div>
          <h2 className="font-serif text-2xl text-[#1A242B]">Center session packages</h2>
          <p className="text-xs text-[#4A5D6B]">Bundle your center's services into packages. Live immediately on your storefront.</p>
        </div>
        <button onClick={() => setModal({})} data-testid="mgr-add-package" className="eyw-btn !py-2 text-sm"><Plus size={16} /> Add package</button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {(packages.data || []).map((p) => (
          <div key={p.id} className="eyw-card p-6" data-testid={`mgr-package-${p.id}`}>
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-lg text-[#1A242B]">{p.name}</h3>
                <p className="text-xs text-[#4A5D6B]">{p.sessions} sessions · {money(p.price)} · {p.eligible_service_ids?.length || 0} offering(s)</p>
              </div>
              <span className="text-xs px-2.5 py-1 rounded-full bg-[#5C8A72]/15 text-[#5C8A72]">Live</span>
            </div>
            {p.description && <RichContent preview text={p.description} className="text-xs text-[#8FA89B] mt-2 line-clamp-3" />}
            <div className="flex gap-2 mt-4">
              <button onClick={() => setModal(p)} className="eyw-btn-ghost !py-1.5 !px-3 text-xs"><Pencil size={13} /> Edit</button>
              <button onClick={() => del(p.id)} className="eyw-btn-ghost !py-1.5 !px-3 text-xs !border-red-300 !text-red-600 hover:!bg-red-500"><Trash2 size={13} /> Delete</button>
            </div>
          </div>
        ))}
        {packages.data?.length === 0 && <p className="text-[#4A5D6B] text-sm">No packages yet. Bundle your center's sessions to sell.</p>}
      </div>
      {modal && <PackageFormModal pkg={modal.id ? modal : null} servicesEndpoint="/manager/me/services" createEndpoint="/manager/me/packages" updateBase="/manager/me/packages" onClose={() => setModal(null)} onSaved={refetch} />}
    </div>
  );
}
